import { CONSTANTS } from '@pk/powerkioskutils';

import { Agent } from './agent';
import { Attachment } from './attachment';
import { CompanyAttachmentType } from './company-attachment-type';
import { CompanyCalendlySetting } from './company-calendly-setting';
import { CompanyFeatureFlag } from './company-feature-flag';
import { Country } from './country';
import { EmailAddress } from './email-address';
import { LendingModuleSetting } from './lending-module-setting';
import { LicensePlan } from './license-plan';
import { UsernameFormat } from './username-format';

export class Company {
	public companyId: string;
	public modifiedDate: string;
	public name: string;
	public contactFname: string;
	public contactMiddle: string;
	public contactLname: string;
	public address1: string;
	public address2: string;
	public city: string;
	public zipCode: string;
	public stateId: string;
	public phone: string;
	public phone2: string;
	public fax: string;
	public mobile: string;
	public email: string;
	public supportEmail: string;
	public isActive: boolean;
	public addDate: string;
	public termsOfServiceAttachmentId: string;
	public coverPageAttachmentId: string;
	public pendingUtilityBaseRateTemplateAttachmentId: string;
	public defaultNavbarLogoAttachmentId: string;
	public defaultFooterLogoAttachmentId: string;
	public defaultEmailLogoAttachmentId: string;
	public countryId: number;
	public phoneCountryId: number;
	public phone2CountryId: number;
	public mobileCountryId: number;
	public faxCountryId: number;
	public phoneExt: string;
	public phone2Ext: string;
	public faxExt: string;
	public commissionRate: number;
	public commissionRate2: number;
	public commissionRate3: number;
	public discountFactor: number;
	public isDecoupled: boolean;
	public domainAlias: string;
	public isSaas: boolean;
	public automatedUserId: string;
	public defaultAgentComplianceDropPredictionThreshold: number;
	public defaultQuickPayRate: number;
	public defaultQuickPayRate2: number;
	public defaultQuickPayRate3: number;
	public minPayment: number;
	public utilityAccountNumThreshold: number;
	public dbaNameThreshold: number;
	public addressLineThreshold: number;
	public allowCommercialMatrixQuote: boolean;
	public allowResidentialMatrixQuote: boolean;
	public residentialQuoteRequiresPOA: boolean;
	public allowInstantQuote: boolean;
	public usernameFormatId: number;
	public showReports: boolean;
	public showCustomerPii: boolean;
	public autoSendAgentWelcomeEmail: boolean;
	public showSapExternalId: boolean;
	public showCommissionRegistrationId: boolean;
	public showResidentialQuoteLinkEmail: boolean;
	public showResidentialQuotePlanEmail: boolean;
	public showResidentialSavings: boolean;
	public showResidentialPreferredContactMethod: boolean;
	public autoSubmitResidentialEnrollment: boolean;
	public defaultMinShortTermPackingPercent: number;
	public defaultMaxShortTermPackingPercent: number;

	// alias for displayLogoAttachment
	public navbarLogoAttachment: Attachment;
	public footerLogoAttachment: Attachment;

	public manuals: Attachment[][];
	public gasMaps: Attachment[][];
	public elecMaps: Attachment[][];
	public images: Attachment[][];
	public defaultNavbarLogoAttachment: Attachment;
	public defaultFooterLogoAttachment: Attachment;
	public defaultEmailLogoAttachment: Attachment;
	public houseAccount: Agent;
	public country: Country;
	public phoneCountry: Country;
	public phone2Country: Country;
	public mobileCountry: Country;
	public faxCountry: Country;
	public emailAddresses: EmailAddress[];
	public calendlySettings: CompanyCalendlySetting[];
	public lendingModuleSetting: LendingModuleSetting;
	public licensePlans: LicensePlan[];
	public featureFlags: CompanyFeatureFlag[];
	public termsOfServiceAttachment: Attachment;
	public coverPageAttachment: Attachment;
	public pendingUtilityBaseRateTemplateAttachment: Attachment;
	public usernameFormat: UsernameFormat;
	public attachmentTypes: CompanyAttachmentType[];

	constructor(company?: Company) {
		Object.assign(this, company);

		this.houseAccount = this.houseAccount ? new Agent(this.houseAccount) : this.houseAccount;
		this.featureFlags = this.featureFlags?.map(ff => new CompanyFeatureFlag(ff)) ?? [];
	}

	get hasMatrixPricing(): boolean {
		return !this.isSaas || this.licensePlans?.some(l => CONSTANTS.licensePlans.matrixPricing.includes(l.id));
	}

	get hasCustomPricing(): boolean {
		return !this.isSaas || this.licensePlans?.some(l => CONSTANTS.licensePlans.customPricing.includes(l.id));
	}

	get hasBufferMargin(): boolean {
		return !this.isSaas || this.licensePlans?.some(l => CONSTANTS.licensePlans.bufferMargin.includes(l.id));
	}

	get hasMarketAnalytics(): boolean {
		return !this.isSaas || this.licensePlans?.some(l => CONSTANTS.licensePlans.marketAnalytics.includes(l.id));
	}

	get hasLayeredPurchasing(): boolean {
		return !this.isSaas || this.licensePlans?.some(l => CONSTANTS.licensePlans.layeredPurchasing.includes(l.id));
	}

	get hasAccountUsage(): boolean {
		return !this.isSaas || this.licensePlans?.some(l => CONSTANTS.licensePlans.accountUsage.includes(l.id));
	}

	get hasCommission(): boolean {
		return !this.isSaas || this.licensePlans?.some(l => CONSTANTS.licensePlans.commission.includes(l.id));
	}
}
